import { useState } from "react";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import WallpaperOutlinedIcon from "@mui/icons-material/WallpaperOutlined";
import AnimationOutlinedIcon from "@mui/icons-material/AnimationOutlined";
import AutoAwesomeMotionOutlinedIcon from "@mui/icons-material/AutoAwesomeMotionOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import { DESIGN_SUGGESTIONS } from "../../../canvas/design/designIntent";
import ThemeTab from "./ThemeTab";
import BackgroundTab from "./BackgroundTab";
import TransitionTab from "./TransitionTab";
import AnimationTab from "./AnimationTab";
import "./cinema.css";

export const STUDIO_TABS = [
  { id: "design", label: "Design", icon: <PaletteOutlinedIcon /> },
  { id: "background", label: "Background", icon: <WallpaperOutlinedIcon /> },
  { id: "transitions", label: "Transitions", icon: <AutoAwesomeMotionOutlinedIcon /> },
  { id: "animations", label: "Animate", icon: <AnimationOutlinedIcon /> },
  { id: "ai", label: "AI", icon: <AutoAwesomeOutlinedIcon /> },
];

function AiTab({ onDesignRequest }) {
  const [text, setText] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const send = async (value) => {
    const request = String(value ?? text).trim();
    if (!request || busy) return;
    setText("");
    setBusy(true);
    setLog((l) => [...l, { role: "user", text: request }]);
    try {
      const reply = await onDesignRequest(request);
      setLog((l) => [...l, { role: "assistant", text: reply?.text || "Done.", list: reply?.changes }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="cine-studio-sub">
        Describe the look you want. Changes apply right away and can be undone. The same requests
        also work in the Microlab Assistant chat while this editor is open.
      </p>
      <textarea
        className="cine-ai-input"
        value={text}
        placeholder="e.g. Apply a cinematic medical theme and add smooth transitions"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        aria-label="Design request"
      />
      <button
        type="button"
        className="cine-btn-primary"
        style={{ width: "100%", marginTop: 8 }}
        onClick={() => send()}
        disabled={busy || !text.trim()}
      >
        {busy ? "Working…" : "Apply"}
      </button>
      <span className="cine-studio-label">Try</span>
      <div className="cine-chips">
        {DESIGN_SUGGESTIONS.map((s) => (
          <button key={s} type="button" className="cine-chip" onClick={() => send(s)} disabled={busy}>
            {s}
          </button>
        ))}
      </div>
      {log.length ? (
        <div className="cine-ai-log" aria-live="polite">
          {log.map((m, i) => (
            <div key={i} className={`cine-ai-msg ${m.role === "user" ? "is-user" : "is-assistant"}`}>
              {m.text}
              {m.list?.length ? (
                <ul>
                  {m.list.map((c, j) => (
                    <li key={j}>{c}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

/**
 * Right-hand design studio: themes, backgrounds, transitions, animations, AI.
 * All persistence goes through the callbacks owned by the Presentation Maker.
 */
export default function DesignStudio({
  tab,
  onTabChange,
  design,
  themeTransition,
  currentElements,
  previousElements,
  slideCount,
  currentIndex,
  uploadedImages,
  classicBackgrounds,
  classicColors,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onApplyTheme,
  onApplyBackground,
  onApplyTransition,
  onUpdateElements,
  onSelectElement,
  onPreviewAnimations,
  onPresentHere,
  onUploadImage,
  onDesignRequest,
}) {
  return (
    <aside className="cine-studio" aria-label="Design studio">
      <div className="cine-studio-tabs" role="tablist">
        {STUDIO_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`cine-studio-tab${tab === t.id ? " is-active" : ""}`}
            onClick={() => onTabChange(t.id)}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>
      <div className="cine-studio-body">
        <div className="cine-row">
          <h3 className="cine-studio-heading cine-grow">{STUDIO_TABS.find((t) => t.id === tab)?.label}</h3>
          <button type="button" className="cine-icon-btn" onClick={onUndo} disabled={!canUndo} aria-label="Undo design change" title="Undo design change">
            <UndoRoundedIcon fontSize="inherit" />
          </button>
          <button type="button" className="cine-icon-btn" onClick={onRedo} disabled={!canRedo} aria-label="Redo design change" title="Redo design change">
            <RedoRoundedIcon fontSize="inherit" />
          </button>
        </div>
        {tab === "design" ? (
          <ThemeTab activeThemeId={design?.themeId} onApplyTheme={onApplyTheme} slideCount={slideCount} />
        ) : null}
        {tab === "background" ? (
          <BackgroundTab
            currentBackground={design?.background}
            currentElements={currentElements}
            slideCount={slideCount}
            currentIndex={currentIndex}
            onApplyBackground={onApplyBackground}
            onUploadImage={onUploadImage}
            uploadedImages={uploadedImages}
            classicBackgrounds={classicBackgrounds}
            classicColors={classicColors}
          />
        ) : null}
        {tab === "transitions" ? (
          <TransitionTab
            currentTransition={design?.transition || themeTransition}
            isThemeDefault={!design?.transition && Boolean(themeTransition)}
            previousElements={previousElements}
            currentElements={currentElements}
            slideCount={slideCount}
            currentIndex={currentIndex}
            onApplyTransition={onApplyTransition}
          />
        ) : null}
        {tab === "animations" ? (
          <AnimationTab
            elements={currentElements}
            onUpdateElements={onUpdateElements}
            onSelectElement={onSelectElement}
            onPreview={onPreviewAnimations}
            onPresentHere={onPresentHere}
          />
        ) : null}
        {tab === "ai" ? <AiTab onDesignRequest={onDesignRequest} /> : null}
      </div>
    </aside>
  );
}
